// Draft-survey helpers for the survey builder: building blank questions, keeping
// every question in step with the selected languages, validation and save cleanup.
// Pure functions only — no React, no redux.

import { typeNeedsOptions } from './survey-question-types';

let keySeed = 0;
// Client-only stable id for list keys / drag-and-drop. Stripped before saving.
export const newQuestionKey = () => `q-${Date.now()}-${keySeed++}`;

const langCodes = (languages = []) => languages.map((l) => l.value);

const pickLangs = (obj, codes, fallback) =>
    codes.reduce((acc, code) => {
        acc[code] = obj?.[code] !== undefined ? obj[code] : fallback();
        return acc;
    }, {});

// Number of option "slots" — options line up by index across languages.
const optionSlotCount = (q, codes) =>
    Math.max(0, ...codes.map((c) => (Array.isArray(q.options?.[c]) ? q.options[c].length : 0)));

const padTo = (arr, n) => {
    const out = Array.isArray(arr) ? [...arr] : [];
    while (out.length < n) out.push('');
    return out;
};

export const createEmptyQuestion = (languages) => {
    const codes = langCodes(languages);
    return {
        _key: newQuestionKey(),
        text: pickLangs({}, codes, () => ''),
        type: '',
        options: pickLangs({}, codes, () => []),
        optional: false,
    };
};

// Add missing language keys / drop removed ones on text, options, scale labels and matrix rows.
export const syncQuestionLanguages = (question, languages) => {
    const codes = langCodes(languages);
    const slots = optionSlotCount(question, codes);
    const q = {
        ...question,
        _key: question._key || newQuestionKey(),
        text: pickLangs(question.text, codes, () => ''),
        options: codes.reduce((acc, c) => {
            acc[c] = padTo(question.options?.[c], slots);
            return acc;
        }, {}),
    };
    if (question.scaleLabels) {
        q.scaleLabels = pickLangs(question.scaleLabels, codes, () => ['', '', '', '', '']);
    }
    if (Array.isArray(question.items)) {
        q.items = question.items.map((item) => ({ ...item, text: pickLangs(item.text, codes, () => '') }));
    }
    return q;
};

const blank = (v) => !String(v ?? '').trim();

// A question the author hasn't started — ignored on save rather than flagged.
export const isQuestionBlank = (q) =>
    !q.type && Object.values(q.text || {}).every(blank);

/**
 * @returns {{ messages: string[], missingLangs: string[] }}
 *   messages — human readable problems, empty when valid
 *   missingLangs — language codes with missing text, so the card can flag the right tab
 */
export const validateQuestion = (q, languages) => {
    const codes = langCodes(languages);
    const messages = [];
    const missing = new Set();

    const textMissing = codes.filter((c) => blank(q.text?.[c]));
    textMissing.forEach((c) => missing.add(c));
    if (textMissing.length) messages.push('Question text is missing');

    if (!q.type) messages.push('Choose an answer type');

    if (typeNeedsOptions(q.type)) {
        const slots = optionSlotCount(q, codes);
        if (slots === 0) {
            messages.push('Add at least one answer option');
        } else {
            const optMissing = codes.filter((c) => padTo(q.options?.[c], slots).some(blank));
            optMissing.forEach((c) => missing.add(c));
            if (optMissing.length) messages.push('Some answer options are empty');
        }
    }

    if (q.type === 'rating' && !(q.starsCount > 0)) messages.push('Set the number of stars');

    if (q.type === 'matrix-scale5') {
        if (!q.items?.length) {
            messages.push('Add at least one row to rate');
        } else {
            const rowMissing = codes.filter((c) => q.items.some((it) => blank(it.text?.[c])));
            rowMissing.forEach((c) => missing.add(c));
            if (rowMissing.length) messages.push('Some grid rows are empty');
        }
    }

    return { messages, missingLangs: [...missing] };
};

export const validateSurvey = ({ groupTitle, languages, questions }) => {
    const surveyErrors = [];
    if (blank(groupTitle)) surveyErrors.push('Give the survey a name');
    if (!languages?.length) surveyErrors.push('Choose at least one language');

    const real = questions.filter((q) => !isQuestionBlank(q));
    if (real.length === 0) surveyErrors.push('Add at least one question');

    const questionErrors = {};
    questions.forEach((q) => {
        if (isQuestionBlank(q)) return;
        const result = validateQuestion(q, languages);
        if (result.messages.length) questionErrors[q._key] = result;
    });

    return {
        surveyErrors,
        questionErrors,
        isValid: surveyErrors.length === 0 && Object.keys(questionErrors).length === 0,
    };
};

// Drop blank questions and client-only fields; keeps the persisted shape unchanged.
export const cleanQuestionsForSave = (questions) =>
    questions
        .filter((q) => !isQuestionBlank(q))
        .map(({ _key, ...q }) => q);

// Turn a saved survey into a fresh draft (used by Duplicate).
export const surveyToDraft = (survey) => {
    const languages = Array.isArray(survey?.languages) && survey.languages.length
        ? survey.languages
        : [{ label: 'English', value: 'en' }];
    const clone = JSON.parse(JSON.stringify(survey?.questions || []));
    return {
        groupTitle: survey?.groupTitle ? `${survey.groupTitle} (copy)` : '',
        surveyType: survey?.surveyType || 'arrivals',
        languages,
        questions: clone.map(({ _id, ...q }) => syncQuestionLanguages(q, languages)),
    };
};
